import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Loader2, Clock, CheckCircle, XCircle, Eye, Ban, MessageSquare, BookOpen } from "lucide-react";
import { format } from "date-fns";
import { useAdminMentorshipRequests, MentorshipRequest } from "@/hooks/useMentorshipRequests";
import { CancelMentorshipRequestDialog } from "@/components/mentorship/CancelMentorshipRequestDialog";
import { getStorageUrl } from "@/lib/storage";

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: typeof Clock }> = {
  pending: { label: "Pending", color: "bg-amber-500", icon: Clock },
  accepted: { label: "Accepted", color: "bg-green-500", icon: CheckCircle },
  declined: { label: "Declined", color: "bg-red-500", icon: XCircle },
  cancelled: { label: "Cancelled", color: "bg-muted", icon: Ban },
};

export function DirectMentorshipRequestsManager() {
  const [statusFilter, setStatusFilter] = useState<string>("pending");
  const [selectedRequest, setSelectedRequest] = useState<MentorshipRequest | null>(null);
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState<MentorshipRequest | null>(null);

  const { data: requests, isLoading } = useAdminMentorshipRequests(statusFilter);

  const handleViewDetail = (request: MentorshipRequest) => {
    setSelectedRequest(request);
    setShowDetailDialog(true);
  };

  const handleCancelClick = (request: MentorshipRequest) => {
    setRequestToCancel(request);
    setShowCancelDialog(true);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Direct Mentorship Requests
          </CardTitle>
          <CardDescription>
            Review direct mentorship requests from entrepreneurs to specific mentors
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex items-center gap-4 mb-4">
            <Label>Status:</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Requests</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="declined">Declined</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Request List */}
          {requests && requests.length > 0 ? (
            <div className="border rounded-lg divide-y">
              {requests.map((request) => {
                const config = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
                const StatusIcon = config.icon;
                const entrepreneur = request.entrepreneur;
                const mentor = request.mentor;

                return (
                  <div
                    key={request.id}
                    className="p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      <div className="flex -space-x-2">
                        <Avatar className="h-10 w-10 border-2 border-background">
                          <AvatarImage src={getStorageUrl("avatars", entrepreneur?.avatar_url) || undefined} />
                          <AvatarFallback>
                            {entrepreneur?.full_name?.charAt(0) || "?"}
                          </AvatarFallback>
                        </Avatar>
                        <Avatar className="h-10 w-10 border-2 border-background">
                          <AvatarImage src={getStorageUrl("avatars", mentor?.avatar_url) || undefined} />
                          <AvatarFallback>
                            {mentor?.full_name?.charAt(0) || "?"}
                          </AvatarFallback>
                        </Avatar>
                      </div>
                      <div>
                        <p className="font-medium">
                          {entrepreneur?.full_name || "Unknown"} → {mentor?.full_name || "Unknown"}
                        </p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            <BookOpen className="h-3 w-3 mr-1" />
                            {request.topic?.name || "No topic"}
                          </Badge>
                          <span className="text-xs text-muted-foreground">
                            {format(new Date(request.created_at), "MMM d, yyyy 'at' h:mm a")}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge variant="secondary" className={`${config.color} text-white`}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {config.label}
                      </Badge>
                      <Button variant="outline" size="sm" onClick={() => handleViewDetail(request)}>
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                      {request.status !== "cancelled" && (
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => handleCancelClick(request)}
                        >
                          <Ban className="h-4 w-4 mr-1" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No {statusFilter !== "all" ? statusFilter : ""} requests found</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Request Details</DialogTitle>
            <DialogDescription>
              View details of this mentorship request
            </DialogDescription>
          </DialogHeader>

          {selectedRequest && (
            <ScrollArea className="max-h-[60vh]">
              <div className="space-y-6 pr-4">
                {/* Entrepreneur Info */}
                <div>
                  <Label className="text-muted-foreground text-xs">Entrepreneur</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={getStorageUrl("avatars", selectedRequest.entrepreneur?.avatar_url) || undefined} />
                      <AvatarFallback>
                        {selectedRequest.entrepreneur?.full_name?.charAt(0) || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <p className="font-medium">{selectedRequest.entrepreneur?.full_name}</p>
                  </div>
                </div>

                {/* Mentor Info */}
                <div>
                  <Label className="text-muted-foreground text-xs">Mentor</Label>
                  <div className="flex items-center gap-3 mt-1">
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={getStorageUrl("avatars", selectedRequest.mentor?.avatar_url) || undefined} />
                      <AvatarFallback>
                        {selectedRequest.mentor?.full_name?.charAt(0) || "?"}
                      </AvatarFallback>
                    </Avatar>
                    <p className="font-medium">{selectedRequest.mentor?.full_name}</p>
                  </div>
                </div>

                <Separator />

                {/* Request Details */}
                <div className="space-y-4">
                  <div>
                    <Label className="text-muted-foreground text-xs">Topic</Label>
                    <p className="font-medium">{selectedRequest.topic?.name || "No topic"}</p>
                  </div>

                  <div>
                    <Label className="text-muted-foreground text-xs">Message</Label>
                    <p className="text-sm whitespace-pre-wrap bg-muted/50 p-3 rounded-lg mt-1">
                      {selectedRequest.message}
                    </p>
                  </div>

                  <div>
                    <Label className="text-muted-foreground text-xs">Status</Label>
                    <div className="mt-1">
                      <Badge
                        variant="secondary"
                        className={`${STATUS_CONFIG[selectedRequest.status]?.color || "bg-muted"} text-white`}
                      >
                        {STATUS_CONFIG[selectedRequest.status]?.label || selectedRequest.status}
                      </Badge>
                    </div>
                  </div>

                  <div>
                    <Label className="text-muted-foreground text-xs">Created</Label>
                    <p className="text-sm">
                      {format(new Date(selectedRequest.created_at), "MMM d, yyyy 'at' h:mm a")}
                    </p>
                  </div>

                  {selectedRequest.status === "cancelled" && selectedRequest.cancelled_at && (
                    <>
                      <Separator />
                      <div className="bg-muted/50 p-4 rounded-lg space-y-2">
                        <p className="font-medium text-sm">Cancellation Details</p>
                        <div>
                          <Label className="text-muted-foreground text-xs">Cancelled on</Label>
                          <p className="text-sm">
                            {format(new Date(selectedRequest.cancelled_at), "MMM d, yyyy 'at' h:mm a")}
                          </p>
                        </div>
                        <div>
                          <Label className="text-muted-foreground text-xs">Cancelled by</Label>
                          <p className="text-sm capitalize">
                            {selectedRequest.cancellation_actor || "Unknown"}
                          </p>
                        </div>
                        {selectedRequest.cancellation_reason && (
                          <div>
                            <Label className="text-muted-foreground text-xs">Reason</Label>
                            <p className="text-sm">{selectedRequest.cancellation_reason}</p>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Actions */}
                {selectedRequest.status !== "cancelled" && (
                  <>
                    <Separator />
                    <div className="flex gap-2">
                      <Button
                        variant="destructive"
                        onClick={() => {
                          setShowDetailDialog(false);
                          handleCancelClick(selectedRequest);
                        }}
                      >
                        <Ban className="h-4 w-4 mr-1" />
                        Cancel Request
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      {requestToCancel && (
        <CancelMentorshipRequestDialog
          open={showCancelDialog}
          onOpenChange={setShowCancelDialog}
          requestId={requestToCancel.id}
          isAdmin={true}
        />
      )}
    </div>
  );
}
